/** @format */

import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import DataApi from '../../api/DataApi';
import GenerateDocumentApi from '../../api/GenerateDocumentApi';
import EditLayoutModal from '../layout/EditLayoutModal';
import { formatDate, formatDateTime, formatTime } from '../../utils/formatUtils';
import './AiChatbot.css';

/** Object options for "create record" (label -> objectName) */
const CREATE_RECORD_OBJECTS = [
  { label: 'Lead', objectName: 'lead' },
  { label: 'Opportunity', objectName: 'opportunity' },
  { label: 'Quote', objectName: 'quote' },
  { label: 'Contact', objectName: 'contact' },
  { label: 'Account', objectName: 'account' },
  { label: 'Task', objectName: 'task' },
];

/** Match path like /view/lead/:id to get current lead context */
const getLeadContextFromPath = (pathname) => {
  if (!pathname || typeof pathname !== 'string') return null;
  const match = pathname.match(/^\/view\/lead\/([^/]+)/i);
  return match ? match[1].trim() : null;
};

/** Match path like /view/opportunity/:id to get current opportunity context */
const getOpportunityContextFromPath = (pathname) => {
  if (!pathname || typeof pathname !== 'string') return null;
  const match = pathname.match(/^\/view\/opportunity\/([^/]+)/i);
  return match ? match[1].trim() : null;
};

/** Match path like /view/quote/:id to get current quote context */
const getQuoteContextFromPath = (pathname) => {
  if (!pathname || typeof pathname !== 'string') return null;
  const match = pathname.match(/^\/view\/quote\/([^/]+)/i);
  return match ? match[1].trim() : null;
};

/** Parse user reply to "choose object" step: returns 'lead' | 'opportunity' | 'quote' | null */
const parseDocumentObjectChoice = (text) => {
  const t = (text || '').trim().toLowerCase();
  if (/^leads?$/.test(t)) return 'lead';
  if (/^opportunit(y|ies)$/.test(t)) return 'opportunity';
  if (/^quotes?$/.test(t)) return 'quote';
  return null;
};

/** Detect if user is asking to generate a quote document */
const isGenerateQuoteIntent = (text) => {
  const t = (text || '').trim().toLowerCase();
  return (
    /\bgenerate\s*(a\s+)?quote\b/.test(t) ||
    /\bquote\s*document\b/.test(t) ||
    /\bcreate\s*(a\s+)?quote\s*(pdf|document)?\b/.test(t) ||
    /\bdownload\s*(a\s+)?quote\b/.test(t) ||
    /\bget\s*(a\s+)?quote\s*(pdf|document)?\b/.test(t)
  );
};

/** Detect if user is asking to generate/download a document (e.g. for opportunity) */
const isGenerateDocumentIntent = (text) => {
  const t = (text || '').trim().toLowerCase();
  return (
    /\bgenerate\s*(a\s+)?document\b/.test(t) ||
    /\bdownload\s*(a\s+)?document\b/.test(t) ||
    /\bget\s*(a\s+)?document\b/.test(t) ||
    /\bcreate\s*(a\s+)?document\b/.test(t) ||
    (/\bdocument\b/.test(t) && (/\bgenerate\b/.test(t) || /\bdownload\b/.test(t) || /\bget\b/.test(t)))
  );
};

/**
 * Single intelligent detector for "create/insert/add/new record" intent.
 * Handles many phrasings and extracts object name when present.
 * @param {string} text - User message
 * @returns {{ isCreateIntent: boolean, objectName: string | null }}
 */
const detectCreateRecordIntent = (text) => {
  const t = (text || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!t) return { isCreateIntent: false, objectName: null };

  const verbMatch = t.match(/^(create|add|insert|new)\b/);
  if (!verbMatch) return { isCreateIntent: false, objectName: null };

  // 1. "create record for school" / "add record for opportunity" / "insert record for lead"
  let m = t.match(/\b(?:create|add|insert|new)\s+(?:a\s+)?(?:new\s+)?record\s+for\s+(\w+)/);
  if (m && m[1]) return { isCreateIntent: true, objectName: m[1] };

  // 2. "create school record" / "add opportunity record" / "insert lead record" / "new contact record"
  m = t.match(/\b(?:create|add|insert|new)\s+(?:a\s+)?(?:new\s+)?(\w+)\s+record\b/);
  if (m && m[1] && m[1] !== 'record') return { isCreateIntent: true, objectName: m[1] };

  // 3. "create record" / "insert record" / "add record" / "new record" (no object)
  if (/^(create|add|insert|new)\s+(?:a\s+)?(?:new\s+)?record?s?$/.test(t)) {
    return { isCreateIntent: true, objectName: null };
  }

  // 4. "create lead" / "insert opportunity" / "new school" / "add a contact" / "create a new task"
  m = t.match(/\b(?:create|add|insert|new)\s+(?:a\s+)?(?:new\s+)?(\w+)s?\b/);
  if (m && m[1]) {
    const obj = m[1];
    if (obj === 'record') return { isCreateIntent: true, objectName: null };
    return { isCreateIntent: true, objectName: obj };
  }

  return { isCreateIntent: true, objectName: null };
};

/** Parse user reply when we asked "which object to create": return objectName or null */
const parseCreateObjectChoice = (text) => {
  const t = (text || '').trim().toLowerCase();
  const found = CREATE_RECORD_OBJECTS.find(
    (o) => o.objectName === t || o.label.toLowerCase() === t || new RegExp(`^${o.objectName}s?$`).test(t)
  );
  return found ? found.objectName : null;
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const STORAGE_KEY = 'ai-sql-chatbot-history';

const loadHistory = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const PARSED_TEXT_STORAGE_MAX = 12000;

/** Strip blob URLs from messages before persisting (they are session-only and invalid after refresh) */
const messagesForStorage = (messages) => {
  if (!Array.isArray(messages)) return messages;
  return messages.map((m) => {
    if (m && m.attachmentBlobUrl) {
      const { attachmentBlobUrl, ...rest } = m;
      return { ...rest, attachmentFilename: m.attachmentFilename };
    }
    if (m && m.documentParse) {
      let next = { ...m };
      if (typeof next.parsedText === 'string' && next.parsedText.length > PARSED_TEXT_STORAGE_MAX) {
        next.parsedText = `${next.parsedText.slice(0, PARSED_TEXT_STORAGE_MAX)}… [truncated for storage]`;
        next.parsedTextStorageTruncated = true;
      }
      if (
        typeof next.transformedOutput === 'string' &&
        next.transformedOutput.length > PARSED_TEXT_STORAGE_MAX
      ) {
        next.transformedOutput = `${next.transformedOutput.slice(0, PARSED_TEXT_STORAGE_MAX)}… [truncated for storage]`;
        next.transformedOutputStorageTruncated = true;
      }
      return next;
    }
    return m;
  });
};

const saveHistory = (messages) => {
  try {
    if (messages.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messagesForStorage(messages)));
  } catch {
    // ignore quota or parse errors
  }
};

const AiChatbot = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => loadHistory());
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Running query…');
  const [fullView, setFullView] = useState(null);
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  /** Unified document flow when not on detail page: 'object' = choose Lead/Opportunity/Quote, 'id' = provide record ID */
  const [pendingDocumentStep, setPendingDocumentStep] = useState(null);
  const [pendingDocumentObjectName, setPendingDocumentObjectName] = useState(null);
  /** Create record: when true, next message is treated as object choice (or user can click quick reply) */
  const [pendingCreateObject, setPendingCreateObject] = useState(false);
  /** Open EditLayoutModal for creating a record (objectName set when opening) */
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalObjectName, setCreateModalObjectName] = useState(null);
  /** Upload pipeline: plain extraction vs AI-structured JSON/CSV */
  const [fileOutputFormat, setFileOutputFormat] = useState('raw');
  const [fileInstruction, setFileInstruction] = useState('');
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const currentLeadId = getLeadContextFromPath(location?.pathname);
  const currentOpportunityId = getOpportunityContextFromPath(location?.pathname);
  const currentQuoteId = getQuoteContextFromPath(location?.pathname);

  useEffect(() => {
    saveHistory(messages);
  }, [messages]);

  // Revoke blob URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      (messagesRef.current || []).forEach((m) => {
        if (m && m.attachmentBlobUrl && typeof m.attachmentBlobUrl === 'string') {
          try {
            URL.revokeObjectURL(m.attachmentBlobUrl);
          } catch (_) {}
        }
      });
    };
  }, []);

  const scrollToBottom = (smooth = true) => {
    const el = messagesContainerRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
      setShowJumpToBottom(false);
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const el = messagesContainerRef.current;
    if (el) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.scrollTop = el.scrollHeight;
          setShowJumpToBottom(false);
        });
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    scrollToBottom(true);
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const threshold = 80;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
    setShowJumpToBottom((prev) => (atBottom ? false : true));
  };

  const CHAT_UPLOAD_MAX_BYTES = 35 * 1024 * 1024;

  const downloadTextFile = (content, filename) => {
    const mime = filename.endsWith('.json')
      ? 'application/json;charset=utf-8'
      : 'text/csv;charset=utf-8';
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleAttachClick = () => {
    if (loading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || loading) return;
    if (file.size > CHAT_UPLOAD_MAX_BYTES) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          error: `File is too large (max ${Math.round(CHAT_UPLOAD_MAX_BYTES / (1024 * 1024))} MB).`,
        },
      ]);
      return;
    }

    const instr = (fileInstruction || '').trim();
    const fmt = fileOutputFormat === 'json' || fileOutputFormat === 'csv' ? fileOutputFormat : 'raw';
    const userParts = [`Uploaded file: ${file.name}`];
    if (fmt !== 'raw') {
      userParts.push(`Output: ${fmt.toUpperCase()}`);
      if (instr) userParts.push(`Instruction: ${instr}`);
    }
    setMessages((prev) => [...prev, { role: 'user', text: userParts.join(' · ') }]);
    setLoadingMessage(fmt === 'raw' ? 'Reading file…' : 'Extracting and generating…');
    setLoading(true);

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const dataUrl = reader.result;
        if (typeof dataUrl !== 'string') {
          throw new Error('Could not read file.');
        }
        const comma = dataUrl.indexOf(',');
        const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
        const api = new DataApi('objectconfig');
        const res = await api.post('/ai-chat/parse-file', {
          fileBase64: base64,
          filename: file.name,
          outputFormat: fmt,
          instruction: instr,
        });
        const data = res?.data;
        if (data?.success && typeof data.fullText === 'string') {
          const stats = data.stats;
          const summaryParts = [];
          if (data.kind === 'pdf' && data.pageCount != null) {
            summaryParts.push(`${data.pageCount} page${data.pageCount !== 1 ? 's' : ''}`);
            if (stats && typeof stats === 'object') {
              if (stats.textPages != null) summaryParts.push(`${stats.textPages} text`);
              if (stats.ocrPages != null) summaryParts.push(`${stats.ocrPages} OCR`);
              if (stats.visionPages != null) summaryParts.push(`${stats.visionPages} AI vision`);
            }
          } else if (data.charCount != null) {
            summaryParts.push(`${data.charCount.toLocaleString()} characters`);
          }
          if (data.transformedOutput != null && (data.outputFormat === 'json' || data.outputFormat === 'csv')) {
            summaryParts.push(`AI → ${String(data.outputFormat).toUpperCase()}`);
          }
          const summary = summaryParts.length ? summaryParts.join(' · ') : 'Done';
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              documentParse: true,
              parseFilename: data.filename || file.name,
              parseKind: data.kind || 'file',
              parsedText: data.fullText,
              parseSummary: summary,
              transformedOutput:
                typeof data.transformedOutput === 'string' ? data.transformedOutput : undefined,
              resultFormat:
                data.outputFormat === 'json' || data.outputFormat === 'csv'
                  ? data.outputFormat
                  : null,
              userInstruction: data.instruction || null,
            },
          ]);
          if (fmt !== 'raw') {
            setFileInstruction('');
          }
        } else {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              error: data?.message || 'Could not parse this file.',
            },
          ]);
        }
      } catch (err) {
        const msg =
          err?.response?.data?.message || err?.message || 'Failed to read file. Try another format.';
        setMessages((prev) => [...prev, { role: 'assistant', error: msg }]);
      } finally {
        setLoading(false);
        setLoadingMessage('Running query…');
      }
    };
    reader.onerror = () => {
      setMessages((prev) => [...prev, { role: 'assistant', error: 'Could not read file.' }]);
      setLoading(false);
      setLoadingMessage('Running query…');
    };
    reader.readAsDataURL(file);
  };

  const handleSend = async () => {
    const question = (input || '').trim();
    if (!question || loading) return;

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text: question }]);
    setLoadingMessage('Running query…');
    setLoading(true);

    try {
      const isDocumentOrQuoteIntent = isGenerateQuoteIntent(question) || isGenerateDocumentIntent(question);

      // Create record: waiting for user to choose object (after "create record" without object)
      if (pendingCreateObject) {
        setPendingCreateObject(false);
        const objectName = parseCreateObjectChoice(question) || (question.trim() ? question.trim().toLowerCase() : null);
        if (objectName) {
          const label = CREATE_RECORD_OBJECTS.find((o) => o.objectName === objectName)?.label || objectName;
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', text: `Opening form to create a new ${label} record.` },
          ]);
          setCreateModalObjectName(objectName);
          setShowCreateModal(true);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              error: 'Please choose an object type or type the object name.',
              quickReplyOptions: CREATE_RECORD_OBJECTS.map((o) => o.label),
              quickReplyAction: 'create',
            },
          ]);
          setPendingCreateObject(true);
        }
        setLoading(false);
        return;
      }

      // Create record intent: open modal for specified object or ask to choose
      const createIntent = detectCreateRecordIntent(question);
      if (createIntent.isCreateIntent) {
        const objectName = createIntent.objectName;
        if (objectName) {
          const label = CREATE_RECORD_OBJECTS.find((o) => o.objectName === objectName)?.label || objectName;
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', text: `Opening form to create a new ${label} record.` },
          ]);
          setCreateModalObjectName(objectName);
          setShowCreateModal(true);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              text: 'Which type of record do you want to create?',
              quickReplyOptions: CREATE_RECORD_OBJECTS.map((o) => o.label),
              quickReplyAction: 'create',
            },
          ]);
          setPendingCreateObject(true);
        }
        setLoading(false);
        return;
      }

      // Step 2: We have object type (Lead/Opportunity/Quote), now waiting for the record ID
      if (pendingDocumentStep === 'id' && pendingDocumentObjectName) {
        const objectName = pendingDocumentObjectName;
        setPendingDocumentStep(null);
        setPendingDocumentObjectName(null);
        const recordId = question.trim();
        if (!recordId) {
          const label = objectName === 'lead' ? 'Lead' : objectName === 'opportunity' ? 'Opportunity' : 'Quote';
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', error: `Please provide the ${label} ID (you can copy it from the record URL or list).` },
          ]);
          setLoading(false);
          return;
        }
        const objectLabel = objectName === 'lead' ? 'Lead' : objectName === 'opportunity' ? 'Opportunity' : 'Quote';
        try {
          let result;
          if (objectName === 'lead') {
            result = await GenerateDocumentApi.generateQuoteFromLead(recordId, 'customer quote', { returnForDisplay: true });
          } else if (objectName === 'opportunity') {
            result = await GenerateDocumentApi.generateDocument('opportunity', recordId, 'customer quote', false, { returnForDisplay: true });
          } else {
            result = await GenerateDocumentApi.generateDocument('quote', recordId, 'customer quote', false, { returnForDisplay: true });
          }
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              text: `Document has been generated for the ${objectLabel} you specified. You can view or download it below.`,
              attachmentBlobUrl: result?.blobUrl,
              attachmentFilename: result?.filename || 'document.pdf',
            },
          ]);
        } catch (err) {
          const msg = err?.message || `Failed to generate document for that ${objectLabel}.`;
          setMessages((prev) => [...prev, { role: 'assistant', error: msg }]);
        }
        setLoading(false);
        return;
      }

      // Step 1: We asked "choose Lead / Opportunity / Quote"; user replied with their choice
      if (pendingDocumentStep === 'object') {
        const choice = parseDocumentObjectChoice(question);
        if (choice) {
          setPendingDocumentObjectName(choice);
          setPendingDocumentStep('id');
          const label = choice === 'lead' ? 'Lead' : choice === 'opportunity' ? 'Opportunity' : 'Quote';
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              text: `Please provide the ${label} ID (you can copy it from the record URL or list).`,
            },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              error: 'Please choose one:',
              quickReplyOptions: ['Lead', 'Opportunity', 'Quote'],
            },
          ]);
        }
        setLoading(false);
        return;
      }

      // Generate document/quote: use current page context if on a relevant detail page, else ask to choose object then ID
      if (isDocumentOrQuoteIntent) {
        if (currentLeadId) {
          try {
            const result = await GenerateDocumentApi.generateQuoteFromLead(currentLeadId, 'customer quote', { returnForDisplay: true });
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                text: 'Quote document has been generated for the lead you\'re viewing. You can view or download it below.',
                attachmentBlobUrl: result?.blobUrl,
                attachmentFilename: result?.filename || 'quote.pdf',
              },
            ]);
          } catch (err) {
            const msg = err?.message || 'Failed to generate quote document.';
            setMessages((prev) => [...prev, { role: 'assistant', error: msg }]);
          }
          setLoading(false);
          return;
        }
        if (currentOpportunityId) {
          try {
            const result = await GenerateDocumentApi.generateDocument('opportunity', currentOpportunityId, 'customer quote', false, { returnForDisplay: true });
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                text: 'Document has been generated for the opportunity you\'re viewing. You can view or download it below.',
                attachmentBlobUrl: result?.blobUrl,
                attachmentFilename: result?.filename || 'opportunity_document.pdf',
              },
            ]);
          } catch (err) {
            const msg = err?.message || 'Failed to generate document.';
            setMessages((prev) => [...prev, { role: 'assistant', error: msg }]);
          }
          setLoading(false);
          return;
        }
        if (currentQuoteId) {
          try {
            const result = await GenerateDocumentApi.generateDocument('quote', currentQuoteId, 'customer quote', false, { returnForDisplay: true });
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                text: 'Quote document has been generated for the quote you\'re viewing. You can view or download it below.',
                attachmentBlobUrl: result?.blobUrl,
                attachmentFilename: result?.filename || 'quote.pdf',
              },
            ]);
          } catch (err) {
            const msg = err?.message || 'Failed to generate document.';
            setMessages((prev) => [...prev, { role: 'assistant', error: msg }]);
          }
          setLoading(false);
          return;
        }
        // Not on a detail page: ask to choose object type first (with clickable options)
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: 'Which record do you want to generate a document for?',
            quickReplyOptions: ['Lead', 'Opportunity', 'Quote'],
          },
        ]);
        setPendingDocumentStep('object');
        setLoading(false);
        return;
      }

      const api = new DataApi('objectconfig');
      const res = await api.post('/ai-sql/execute', { question });
      const data = res?.data;

      const wantsChart = /\b(chart|graph|plot|pie|donut)\b/i.test(question);
      const wantsPieOrDonut = /\b(pie|donut)\b/i.test(question);
      const chartType = wantsChart && wantsPieOrDonut ? 'doughnut' : 'bar';

      if (data?.success && Array.isArray(data?.data)) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: data?.message || 'Here are your results.',
            rowCount: data?.rowCount ?? data?.data?.length,
            data: data.data,
            columnLabels: data?.columnLabels,
            showChart: wantsChart,
            chartType,
            objectName: data?.objectName,
          },
        ]);
      } else if (data?.success && data?.data) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: data?.message || 'Here are your results.',
            rowCount: data?.rowCount,
            data: Array.isArray(data.data) ? data.data : [data.data],
            columnLabels: data?.columnLabels,
            showChart: wantsChart,
            chartType,
            objectName: data?.objectName,
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            error: data?.message || 'No data returned.',
          },
        ]);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to run query. Please try again.';
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          error: msg,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /** When user clicks a quick-reply option (Lead / Opportunity / Quote) for document generation */
  const handleDocumentObjectChoice = (option) => {
    const objectName = option.toLowerCase();
    setMessages((prev) => [
      ...prev,
      { role: 'user', text: option },
      {
        role: 'assistant',
        text: `Please provide the ${option} ID (you can copy it from the record URL or list).`,
      },
    ]);
    setPendingDocumentObjectName(objectName);
    setPendingDocumentStep('id');
  };

  /** When user clicks a quick-reply option for create record (Lead, Opportunity, etc.) */
  const handleCreateObjectChoice = (option) => {
    const item = CREATE_RECORD_OBJECTS.find((o) => o.label === option);
    const objectName = item ? item.objectName : option.toLowerCase();
    setMessages((prev) => [
      ...prev,
      { role: 'user', text: option },
      { role: 'assistant', text: `Opening form to create a new ${option} record.` },
    ]);
    setCreateModalObjectName(objectName);
    setShowCreateModal(true);
    setPendingCreateObject(false);
  };

  const getColumnLabel = (col, columnLabels) => {
    if (columnLabels && columnLabels[col]) return columnLabels[col];
    return col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  /**
   * Format cell value for display. Uses same date/datetime/time formatting as DetailLayout (formatUtils).
   * Infers date/datetime from column name or ISO value shape when column metadata is not available.
   */
  const formatCellValue = (col, value) => {
    if (value == null || value === '') return '';
    // Avoid [object Object]: normalize interval-like or plain objects to a displayable value
    if (typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date)) {
      const intervalKeys = ['years', 'months', 'days', 'hours', 'minutes', 'seconds', 'milliseconds'];
      const hasInterval = intervalKeys.some((k) => Object.prototype.hasOwnProperty.call(value, k));
      if (hasInterval) {
        const d = Number(value.days) || 0;
        const h = Number(value.hours) || 0;
        const mi = Number(value.minutes) || 0;
        const totalDays = d + h / 24 + mi / 1440;
        return String(Math.round(totalDays));
      }
      if (typeof value.toPostgres === 'function') return value.toPostgres();
      if (typeof value.toISO === 'function') return value.toISO();
      return '[value]';
    }
    const str = String(value).trim();
    if (!str) return '';
    const colLower = (col || '').toLowerCase();
    const isPercentageOrFloat =
      colLower.includes('percentage') ||
      colLower.includes('percent') ||
      colLower.includes('allocation');
    if (isPercentageOrFloat) {
      const n = typeof value === 'number' ? value : parseFloat(value);
      if (Number.isFinite(n)) return n.toFixed(2);
    }
    const looksLikeDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(str);
    const looksLikeDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str) || /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(str);
    const looksLikeTimeOnly = /^\d{1,2}:\d{2}(:\d{2})?$/.test(str);
    if (colLower.includes('datetime') || colLower === 'createddate' || colLower === 'lastmodifieddate' || looksLikeDateTime) {
      try {
        return formatDateTime(value);
      } catch {
        return str;
      }
    }
    if (colLower.includes('_date') || colLower.endsWith('date') || colLower === 'due_date' || looksLikeDateOnly) {
      try {
        return formatDate(value);
      } catch {
        return str;
      }
    }
    if (colLower.includes('time') && !colLower.includes('datetime') || looksLikeTimeOnly) {
      try {
        return formatTime(value);
      } catch {
        return str;
      }
    }
    return str;
  };

  /**
   * Build chart data from result rows. Prefers owner/name for labels and percentage columns for values.
   * Returns { labels, values, valueLabel } or null if not chartable.
   */
  const buildChartData = (rows, columnLabels) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const first = rows[0];
    const columns = typeof first === 'object' && first !== null ? Object.keys(first) : [];
    if (columns.length < 2) return null;

    const isNumeric = (col) => {
      const val = first[col];
      const n = typeof val === 'number' ? val : parseFloat(val);
      return Number.isFinite(n);
    };
    const isIdLike = (col) =>
      col === 'id' || col === 'ownerid' || col.toLowerCase().endsWith('_id');

    const labelCol =
      columns.find((c) => (c === 'owner' || c === 'name') && !isNumeric(c)) ||
      columns.find((c) => !isIdLike(c) && !isNumeric(c)) ||
      columns[0];
    const numCol =
      columns.find((c) => /percentage|percent|allocation/i.test(c) && isNumeric(c)) ||
      columns.find((c) => isNumeric(c)) ||
      columns[1];

    const labels = rows.map((row) => {
      const v = row[labelCol];
      return v != null ? String(v) : '';
    });
    const values = rows.map((row) => {
      const v = row[numCol];
      const n = typeof v === 'number' ? v : parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    });

    if (labels.length === 0 || values.every((n) => n === 0)) return null;
    return {
      labels,
      values,
      valueLabel: columnLabels?.[numCol] || numCol.replace(/_/g, ' '),
    };
  };

  const CHART_COLORS = [
    'rgba(54, 162, 235, 0.9)',
    'rgba(255, 99, 132, 0.9)',
    'rgba(75, 192, 192, 0.9)',
    'rgba(255, 159, 64, 0.9)',
    'rgba(153, 102, 255, 0.9)',
    'rgba(255, 205, 86, 0.9)',
    'rgba(201, 203, 207, 0.9)',
    'rgba(0, 180, 120, 0.9)',
  ];

  const renderResultChart = (rows, columnLabels, chartType = 'bar') => {
    const chartData = buildChartData(rows, columnLabels);
    if (!chartData) return null;

    const { labels, values, valueLabel } = chartData;
    const colors = labels.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]);

    const data = {
      labels,
      datasets: [
        {
          label: valueLabel,
          data: values,
          backgroundColor: colors,
          borderColor: colors.map((c) => c.replace(/0\.\d+/, '1')),
          borderWidth: 1,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'top' },
        title: { display: false },
      },
      scales:
        chartType === 'bar'
          ? {
              y: { beginAtZero: true },
              x: { maxRotation: 45, minRotation: 0 },
            }
          : undefined,
    };

    return (
      <div className="ai-chatbot-chart-wrap">
        {chartType === 'doughnut' ? (
          <Doughnut data={data} options={options} />
        ) : (
          <Bar data={data} options={options} />
        )}
      </div>
    );
  };

  const openChartFullView = (data, columnLabels, chartType) => {
    setFullView({ type: 'chart', data, columnLabels, chartType: chartType || 'bar' });
  };

  const openTableFullView = (data, columnLabels, objectName) => {
    setFullView({ type: 'table', data, columnLabels, objectName });
  };

  const downloadPdf = async (messageIndex, msg) => {
    if (!msg?.data || !Array.isArray(msg.data) || msg.data.length === 0) return;
    const doc = new jsPDF('p', 'mm', 'a4');
    const margin = 14;
    let currentY = margin;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const maxWidth = pageWidth - 2 * margin;

    try {
      if (msg.showChart) {
        const chartEl = document.querySelector(`[data-ai-chart-index="${messageIndex}"]`);
        if (chartEl) {
          const canvas = await html2canvas(chartEl, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            logging: false,
          });
          const imgData = canvas.toDataURL('image/png');
          const imgWidth = Math.min(maxWidth, (canvas.width * 0.4));
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          if (currentY + imgHeight + 10 > pageHeight - margin) {
            doc.addPage();
            currentY = margin;
          }
          doc.setFontSize(11);
          doc.text('Chart', margin, currentY);
          currentY += 8;
          doc.addImage(imgData, 'PNG', margin, currentY, imgWidth, imgHeight);
          currentY += imgHeight + 14;
        }
      }

      const first = msg.data[0];
      const allColumns = typeof first === 'object' && first !== null ? Object.keys(first) : [];
      const columns = allColumns.filter((col) => {
        if (col === 'id' || col === 'related_object' || col === 'related_record_id') return false;
        const lower = col.toLowerCase();
        if (lower === 'ownerid' || lower === 'owner_id') return false;
        return true;
      });
      if (columns.length === 0) return;

      const headers = columns.map((col) => getColumnLabel(col, msg.columnLabels));
      const body = msg.data.map((row) =>
        columns.map((col) => {
          const val = row[col];
          if (val == null || val === '') return '';
          return String(formatCellValue(col, val));
        })
      );

      if (currentY + 20 > pageHeight - margin) {
        doc.addPage();
        currentY = margin;
      }
      doc.setFontSize(11);
      doc.text('Data', margin, currentY);
      currentY += 8;

      autoTable(doc, {
        head: [headers],
        body,
        startY: currentY,
        margin: { left: margin, right: margin },
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [70, 100, 140], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] },
      });

      const fileName = `AI_Results_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error('PDF export failed', err);
    }
  };

  const renderResultTable = (rows, columnLabels, objectName) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const first = rows[0];
    const allColumns = typeof first === 'object' && first !== null ? Object.keys(first) : [];
    const columns = allColumns.filter((col) => {
      if (col === 'id' || col === 'related_object' || col === 'related_record_id') return false;
      const lower = col.toLowerCase();
      if (lower === 'ownerid' || lower === 'owner_id') return false;
      return true;
    });
    if (columns.length === 0) return <p className="mb-0 text-muted">No columns</p>;

    return (
      <div className="ai-chatbot-table-wrap">
        <Table size="sm" bordered hover responsive className="ai-chatbot-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col}>{getColumnLabel(col, columnLabels)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx}>
                {columns.map((col) => {
                  const value = formatCellValue(col, row[col]);
                  const isNameCol = col === 'name';
                  const isRelatedRecordCol = col === 'related_record';
                  const canLinkName = isNameCol && objectName && row.id != null && row.id !== '';
                  const canLinkRelatedRecord =
                    isRelatedRecordCol &&
                    row.related_object &&
                    row.related_record_id != null &&
                    row.related_record_id !== '';
                  const linkUrl = canLinkName
                    ? `/view/${objectName}/${row.id}`
                    : canLinkRelatedRecord
                      ? `/view/${row.related_object}/${row.related_record_id}`
                      : null;
                  return (
                    <td key={col}>
                      {linkUrl ? (
                        <a
                          href={linkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary text-decoration-underline"
                        >
                          {value || '\u00A0'}
                        </a>
                      ) : (
                        value
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <>
      <button
        type="button"
        className="ai-chatbot-fab"
        onClick={() => setIsOpen((o) => !o)}
        title="Ask your data (AI)"
        aria-label="Open AI data chatbot"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </button>

      {isOpen && (
        <div className="ai-chatbot-panel">
          <div className="ai-chatbot-header">
            <span className="ai-chatbot-title">Ask your data</span>
            <span className="ai-chatbot-header-actions">
              {messages.length > 0 && (
                <Button
                  variant="link"
                  size="sm"
                  className="ai-chatbot-clear"
                  onClick={() => {
                    setMessages([]);
                    saveHistory([]);
                  }}
                  aria-label="Clear history"
                >
                  Clear
                </Button>
              )}
              <Button
                variant="link"
                size="sm"
                className="ai-chatbot-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                ×
              </Button>
            </span>
          </div>

          <div
            ref={messagesContainerRef}
            className="ai-chatbot-messages"
            onScroll={handleMessagesScroll}
          >
            {messages.length === 0 && (
              <div className="ai-chatbot-welcome">
                <p>Ask a question in plain language to query your CRM data.</p>
                <p className="small text-muted">e.g. &quot;List all contacts in Billing accounts&quot;</p>
                <p className="small text-muted">
                  Use the attach button to upload a <strong>PDF</strong> or plain text file. Choose{' '}
                  <strong>Plain text</strong> for extraction only, or <strong>JSON</strong> / <strong>CSV</strong> to
                  describe what you want and get structured output (requires OpenAI on the server).
                </p>
                <p className="small text-muted">Say &quot;chart&quot;, &quot;graph&quot;, &quot;pie&quot;, or &quot;donut&quot; to show results as a chart, e.g. &quot;Pie chart of task allocation by owner&quot;.</p>
                <p className="small text-muted">Results show key fields by default. Ask for specific fields if you need more, e.g. &quot;tasks with description and due date&quot;.</p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`ai-chatbot-msg ai-chatbot-msg-${msg.role}`}>
                {msg.role === 'user' && <div className="ai-chatbot-bubble user">{msg.text}</div>}
                {msg.role === 'assistant' && (
                  <div className="ai-chatbot-bubble assistant">
                    {msg.documentParse && (msg.parsedText != null || msg.transformedOutput != null) && (
                      <div className="ai-chatbot-parse-result">
                        <p className="mb-1 fw-semibold">
                          {msg.parseFilename || 'file'}
                          {msg.parseSummary && (
                            <span className="fw-normal text-muted ms-1">({msg.parseSummary})</span>
                          )}
                        </p>
                        {msg.userInstruction && (
                          <p className="small text-muted mb-1">
                            <span className="fw-semibold text-body">Your instruction: </span>
                            {msg.userInstruction}
                          </p>
                        )}
                        {msg.transformedOutput != null && msg.resultFormat && (
                          <>
                            <p className="small fw-semibold mb-1 mt-2">
                              Result ({msg.resultFormat === 'json' ? 'JSON' : 'CSV'})
                            </p>
                            <div className="ai-chatbot-parse-actions mb-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-1"
                                onClick={() => {
                                  if (navigator.clipboard?.writeText) {
                                    navigator.clipboard.writeText(msg.transformedOutput).catch(() => {});
                                  }
                                }}
                              >
                                Copy result
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                className="me-1"
                                onClick={() => {
                                  const ext = msg.resultFormat === 'json' ? 'json' : 'csv';
                                  const base = (msg.parseFilename || 'export').replace(/\.[^.]+$/, '');
                                  downloadTextFile(msg.transformedOutput, `${base}.${ext}`);
                                }}
                              >
                                Download .{msg.resultFormat}
                              </Button>
                            </div>
                            <pre className="ai-chatbot-parse-pre ai-chatbot-parse-pre--result">
                              {msg.transformedOutput}
                            </pre>
                          </>
                        )}
                        {msg.parsedText != null && msg.parsedText !== '' && (
                          <>
                            <p className="small fw-semibold mb-1 mt-2">
                              {msg.transformedOutput != null
                                ? 'Extracted source text'
                                : 'Extracted text'}
                            </p>
                            <div className="ai-chatbot-parse-actions mb-2">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                className="me-1"
                                onClick={() => {
                                  const t = msg.parsedText || '';
                                  if (navigator.clipboard?.writeText) {
                                    navigator.clipboard.writeText(t).catch(() => {});
                                  }
                                }}
                              >
                                Copy extracted
                              </Button>
                            </div>
                            <pre
                              className={
                                msg.transformedOutput != null
                                  ? 'ai-chatbot-parse-pre ai-chatbot-parse-pre--source'
                                  : 'ai-chatbot-parse-pre'
                              }
                            >
                              {msg.parsedText || ''}
                            </pre>
                          </>
                        )}
                      </div>
                    )}
                    {msg.error && !msg.quickReplyOptions && <p className="mb-0 text-danger">{msg.error}</p>}
                    {msg.text && !msg.error && !msg.documentParse && (
                      <p className="mb-1">
                        {msg.text}
                        {msg.rowCount != null && (
                          <span className="text-muted ms-1">({msg.rowCount} row{msg.rowCount !== 1 ? 's' : ''})</span>
                        )}
                      </p>
                    )}
                    {msg.error && msg.quickReplyOptions && (
                      <p className="mb-2 text-danger">{msg.error}</p>
                    )}
                    {msg.quickReplyOptions && Array.isArray(msg.quickReplyOptions) && (
                      <div className="ai-chatbot-quick-replies mt-2">
                        {msg.quickReplyOptions.map((option) => (
                          <Button
                            key={option}
                            variant="outline-primary"
                            size="sm"
                            className="ai-chatbot-quick-reply-btn me-1 mb-1"
                            onClick={() =>
                              msg.quickReplyAction === 'create'
                                ? handleCreateObjectChoice(option)
                                : handleDocumentObjectChoice(option)
                            }
                          >
                            {option}
                          </Button>
                        ))}
                      </div>
                    )}
                    {msg.attachmentBlobUrl && (
                      <div className="ai-chatbot-doc-attachment mt-2">
                        <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            as="a"
                            href={msg.attachmentBlobUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View PDF
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            as="a"
                            href={msg.attachmentBlobUrl}
                            download={msg.attachmentFilename || 'quote.pdf'}
                          >
                            Download
                          </Button>
                        </div>
                        <div className="ai-chatbot-pdf-embed-wrapper">
                          <iframe
                            title="Generated quote document"
                            src={msg.attachmentBlobUrl}
                            className="ai-chatbot-pdf-embed"
                          />
                        </div>
                      </div>
                    )}
                    {msg.attachmentFilename && !msg.attachmentBlobUrl && (
                      <p className="small text-muted mb-0 mt-1">
                        (Document was generated in a previous session. Generate again to view or download.)
                      </p>
                    )}
                    {msg.data && (
                      <>
                        {msg.showChart && (
                          <div className="ai-chatbot-result-block">
                            <div className="ai-chatbot-result-actions">
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-muted"
                                onClick={() => openChartFullView(msg.data, msg.columnLabels, msg.chartType || 'bar')}
                              >
                                Full view
                              </Button>
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-muted ms-2"
                                onClick={() => downloadPdf(i, msg)}
                              >
                                Download PDF
                              </Button>
                            </div>
                            <div data-ai-chart-index={i}>
                              {renderResultChart(msg.data, msg.columnLabels, msg.chartType || 'bar')}
                            </div>
                          </div>
                        )}
                        <div className="ai-chatbot-result-block">
                          <div className="ai-chatbot-result-actions">
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-muted"
                              onClick={() => openTableFullView(msg.data, msg.columnLabels, msg.objectName)}
                            >
                              Full view
                            </Button>
                            {!msg.showChart && (
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-muted ms-2"
                                onClick={() => downloadPdf(i, msg)}
                              >
                                Download PDF
                              </Button>
                            )}
                          </div>
                          {renderResultTable(msg.data, msg.columnLabels, msg.objectName)}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="ai-chatbot-msg ai-chatbot-msg-assistant">
                <div className="ai-chatbot-bubble assistant">
                  <span className="ai-chatbot-typing">{loadingMessage}</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
            {showJumpToBottom && (
              <button
                type="button"
                className="ai-chatbot-jump-to-bottom"
                onClick={() => scrollToBottom(true)}
                aria-label="Jump to bottom"
                title="Jump to bottom"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M19 12l-7 7-7-7" />
                </svg>
                <span>Bottom</span>
              </button>
            )}
          </div>

          <div className="ai-chatbot-upload-bar">
            <Form.Group className="mb-1">
              <Form.Label className="small text-muted mb-0">File output</Form.Label>
              <Form.Select
                size="sm"
                className="ai-chatbot-format-select"
                value={fileOutputFormat}
                onChange={(e) => setFileOutputFormat(e.target.value)}
                disabled={loading}
              >
                <option value="raw">Plain extracted text</option>
                <option value="json">JSON (AI-structured)</option>
                <option value="csv">CSV (AI-structured)</option>
              </Form.Select>
            </Form.Group>
            {(fileOutputFormat === 'json' || fileOutputFormat === 'csv') && (
              <Form.Group className="mb-0">
                <Form.Label className="small text-muted mb-0">Instructions for the AI (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  className="ai-chatbot-instruction-input"
                  placeholder="e.g. Extract all line items as rows with columns: description, quantity, unit price."
                  value={fileInstruction}
                  onChange={(e) => setFileInstruction(e.target.value)}
                  disabled={loading}
                />
              </Form.Group>
            )}
          </div>

          <div className="ai-chatbot-footer">
            <input
              ref={fileInputRef}
              type="file"
              className="d-none"
              accept=".pdf,.txt,.csv,.md,.json,.tsv,.log,application/pdf,text/plain,text/csv"
              onChange={handleFileChange}
            />
            <Button
              type="button"
              variant="outline-secondary"
              size="sm"
              className="ai-chatbot-attach"
              onClick={handleAttachClick}
              disabled={loading}
              title="Attach PDF or text file"
              aria-label="Attach PDF or text file"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </Button>
            <Form.Control
              ref={inputRef}
              type="text"
              placeholder="Ask a question… (e.g. include description if you need extra fields)"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              className="ai-chatbot-input"
            />
            <Button
              variant="primary"
              size="sm"
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="ai-chatbot-send"
            >
              Send
            </Button>
          </div>
        </div>
      )}

      <Modal
        show={fullView != null}
        onHide={() => setFullView(null)}
        size="xl"
        centered
        className="ai-chatbot-fullview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{fullView?.type === 'chart' ? 'Chart' : 'Table'} – Full view</Modal.Title>
        </Modal.Header>
        <Modal.Body className="ai-chatbot-fullview-body">
          {fullView?.type === 'chart' && fullView.data && (
            <div className="ai-chatbot-chart-wrap ai-chatbot-chart-fullview">
              {renderResultChart(fullView.data, fullView.columnLabels, fullView.chartType || 'bar')}
            </div>
          )}
          {fullView?.type === 'table' && fullView.data && (
            <div className="ai-chatbot-table-wrap ai-chatbot-table-fullview">
              {renderResultTable(fullView.data, fullView.columnLabels, fullView.objectName)}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {showCreateModal && createModalObjectName && (
        <EditLayoutModal
          show={showCreateModal}
          onHide={() => {
            setShowCreateModal(false);
            setCreateModalObjectName(null);
          }}
          objectName={createModalObjectName}
          onSave={() => {
            setShowCreateModal(false);
            setCreateModalObjectName(null);
          }}
          onCancel={() => {
            setShowCreateModal(false);
            setCreateModalObjectName(null);
          }}
        />
      )}
    </>
  );
};

export default AiChatbot;
